
import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { searchProducts } from "@/api/products";
import { useDebounce } from "@/hooks/useDebounce";
import { Product, ProductSearchResponse } from "@/types/product";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useSubscription } from "@/hooks/useSubscription";
import { Crown } from "lucide-react";

export default function Search() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, 300);
  const subscription = useSubscription();

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<ProductSearchResponse>({
    queryKey: ["products", debouncedSearch],
    queryFn: ({ pageParam = 1 }) => 
      searchProducts({ 
        query: debouncedSearch, 
        pageParam: Number(pageParam), 
        pageSize: 10 
      }),
    getNextPageParam: (lastPage) =>
      lastPage.next ? parseInt(lastPage.next.split('page=')[1]) : undefined,
    enabled: debouncedSearch.length >= 2,
    initialPageParam: 1,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleProductClick = (productId: string) => {
    if (!subscription.hasQueryCapacity()) {
      navigate('/upgrade');
      return;
    }
    navigate(`/product/${productId}`);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-3 p-2">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-4 w-[80%]" />
          </div>
        </div>
      ))}
    </div>
  );

  const allResults = data?.pages.flatMap((page) => page.results) ?? [];
  const hasReachedQueryLimit = !subscription.hasQueryCapacity();

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">Product Search</h1>
      <div className="relative">
        <Input
          type="search"
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setDropdownOpen(true)}
          className="w-full"
        />
        {dropdownOpen && searchTerm.length >= 2 && (
          <div className="absolute w-full mt-1 bg-white rounded-lg border shadow-lg z-50">
            <ScrollArea
              className={cn(
                "rounded-lg",
                isLoading || isFetchingNextPage ? "h-[300px]" :
                allResults.length === 0 ? "h-[100px]" : "h-[300px]"
              )}
              onScrollCapture={handleScroll}
            >
              <div className="p-2">
                {isLoading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    {allResults.map((product: Product) => (
                      <div
                        key={product.id}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg transition-all duration-200",
                          "border border-transparent",
                          hasReachedQueryLimit 
                            ? "bg-gray-50 cursor-not-allowed" 
                            : "hover:bg-slate-50 hover:border-slate-200 cursor-pointer"
                        )}
                        onClick={() => handleProductClick(product.id)}
                      >
                        <div className="h-10 w-10 bg-primary-light rounded-lg flex items-center justify-center flex-shrink-0">
                          {hasReachedQueryLimit ? (
                            <Crown className="h-4 w-4 text-yellow-500" />
                          ) : (
                            <span className="text-primary text-sm font-medium">
                              {product.title_en?.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-900 font-medium truncate">
                            {
                              product.title_en
                                .split(new RegExp(`\\b(${searchTerm.split(' ').join('|')})\\b`, 'gi'))
                                .map((fragment, i) => (
                                  searchTerm
                                    .split(' ')
                                    .includes(fragment.toLowerCase()) ?
                                    <mark key={i} style={{backgroundColor: 'yellow', color: 'black'}}>
                                      {fragment}
                                    </mark>
                                    :
                                    fragment
                                ))
                            }
                          </div>
                        </div>
                      </div>
                    ))}
                    {allResults.length === 0 && !isLoading && (
                      <div className="h-[100px] flex items-center justify-center text-slate-500">
                        No results found
                      </div>
                    )}
                    {isFetchingNextPage && <LoadingSkeleton/>}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}